import classNames from 'classnames';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import EvaCardShell from '@/components/eva/EvaCardShell';
import EvaInput from '@/components/eva/EvaInput';
import EvaPasswordInput from '@/components/eva/EvaPasswordInput';
import EvaTabs from '@/components/eva/EvaTabs';
import CaptchaBox from '@/containers/CaptchaBox';

import styles from './index.module.scss';
import useEvaForgotPassword from './use-eva-forgot-password';

const verificationCodeLength = 6;

/**
 * 重置密码单页(EVA 定制):「手机号验证重置 | 邮件验证码重置」双 tab,
 * 标识符 + 验证码 + 新密码 + 确认密码同页完成,替代上游的多页流。
 * 全部状态与交互逻辑在 use-eva-forgot-password.ts,这里只负责渲染。
 */
const EvaForgotPassword = () => {
  const { t } = useTranslation();
  const {
    method,
    methodTabs,
    switchMethod,
    isEmail,
    identifier,
    setIdentifier,
    code,
    setCode,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    identifierError,
    codeError,
    passwordError,
    confirmError,
    isSendingCode,
    isSubmitting,
    countdownSeconds,
    isCountingDown,
    hasSentCode,
    isSendCodeDisabled,
    isSubmitDisabled,
    handleSendCode,
    handleSubmit,
    goToSignIn,
  } = useEvaForgotPassword();

  const sendCodeText = isCountingDown
    ? t('eva_domestic.countdown', { seconds: countdownSeconds })
    : isSendingCode
      ? t('eva_domestic.sending')
      : hasSentCode
        ? t('eva_domestic.resend')
        : t('eva_domestic.send_code');

  return (
    <EvaCardShell variant="wide">
      <EvaTabs
        className={styles.tabs}
        tabs={methodTabs}
        activeKey={method}
        onChange={(key) => {
          // Key 经守卫收窄,无需断言
          if (key === 'phone' || key === 'email') {
            switchMethod(key);
          }
        }}
      />

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.fields}>
          <div className={styles.field}>
            <EvaInput
              name="identifier"
              prefix={isEmail ? undefined : '+86'}
              autoComplete={isEmail ? 'email' : 'tel-national'}
              inputMode={isEmail ? 'email' : 'numeric'}
              placeholder={
                isEmail ? t('eva_domestic.email_placeholder') : t('eva_domestic.phone_placeholder')
              }
              value={identifier}
              isDanger={Boolean(identifierError)}
              onChange={(event) => {
                setIdentifier(
                  isEmail
                    ? event.currentTarget.value
                    : event.currentTarget.value.replaceAll(/\D/g, '').slice(0, 11)
                );
              }}
            />
            {identifierError && <div className={styles.error}>{identifierError}</div>}
          </div>

          <div className={styles.field}>
            <EvaInput
              name="verificationCode"
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder={t('eva_domestic.code_placeholder')}
              value={code}
              isDanger={Boolean(codeError)}
              suffix={
                <button
                  className={classNames(
                    styles.codeButton,
                    isCountingDown && styles.codeButtonCountdown
                  )}
                  disabled={isSendCodeDisabled}
                  type="button"
                  onClick={handleSendCode}
                >
                  {sendCodeText}
                </button>
              }
              onChange={(event) => {
                setCode(
                  event.currentTarget.value.replaceAll(/\D/g, '').slice(0, verificationCodeLength)
                );
              }}
            />
            {codeError && <div className={styles.error}>{codeError}</div>}
          </div>

          <div className={styles.field}>
            <EvaPasswordInput
              name="password"
              autoComplete="new-password"
              placeholder={t('eva_domestic.new_password_placeholder')}
              value={password}
              isDanger={Boolean(passwordError)}
              onChange={(event) => {
                setPassword(event.currentTarget.value);
              }}
            />
            {passwordError && <div className={styles.error}>{passwordError}</div>}
          </div>

          <div className={styles.field}>
            <EvaPasswordInput
              name="confirmPassword"
              autoComplete="new-password"
              placeholder={t('eva_domestic.confirm_password_placeholder')}
              value={confirmPassword}
              isDanger={Boolean(confirmError)}
              onChange={(event) => {
                setConfirmPassword(event.currentTarget.value);
              }}
            />
            {confirmError && <div className={styles.error}>{confirmError}</div>}
          </div>
        </div>

        <CaptchaBox />

        <button className={styles.submit} disabled={isSubmitDisabled} type="submit">
          {isSubmitting ? (
            <>
              <LoaderCircle aria-hidden className={styles.submitLoader} size={20} />
              <span>{t('eva_domestic.submitting')}</span>
            </>
          ) : (
            t('eva_domestic.reset_password')
          )}
        </button>

        <button type="button" className={styles.backLink} onClick={goToSignIn}>
          {t('eva_domestic.back_to_sign_in')}
        </button>
      </form>
    </EvaCardShell>
  );
};

export default EvaForgotPassword;
